/** Permission codes (module:action) — must match nailmap_server AdminPermissions. */
export const PERM = {
  DASHBOARD_READ: "dashboard:read",
  ADMIN_USER_READ: "admin_user:read",
  ADMIN_USER_CREATE: "admin_user:create",
  ADMIN_USER_UPDATE: "admin_user:update",
  ADMIN_USER_DELETE: "admin_user:delete",
  ROLE_READ: "role:read",
  ROLE_MANAGE: "role:manage",
  APP_USER_READ: "app_user:read",
  LOYALTY_READ: "loyalty:read",
  OUTLET_READ: "outlet:read",
  OUTLET_CHANGE_REQUEST_READ: "outlet_change_request:read",
  ORDER_READ: "order:read",
  REVIEW_MODERATE: "review:moderate",
  SUPPORT_READ: "support:read",
  CATEGORY_READ: "category:read",
  CATEGORY_CREATE: "category:create",
  CATEGORY_DELETE: "category:delete",
  COUPON_READ: "coupon:read",
  COUPON_CREATE: "coupon:create",
  COUPON_UPDATE: "coupon:update",
  COUPON_DELETE: "coupon:delete",
  COUPON_APPROVE: "coupon:approve",
  LOG_READ: "log:read",
  LANGUAGE_READ: "language:read",
  APP_TRANSLATION_READ: "app_translation:read",
  OWNER_TRANSLATION_READ: "owner_translation:read",
  MESSAGE_CODE_READ: "message_code:read",
  CANCEL_REASON_READ: "cancel_reason:read",
  CANCEL_REASON_UPDATE: "cancel_reason:update",
  CANCEL_REASON_DELETE: "cancel_reason:delete",
  COUNTRY_READ: "country:read",
  COUNTRY_CREATE: "country:create",
  COUNTRY_UPDATE: "country:update",
  COUNTRY_DELETE: "country:delete",
  PACKAGE_READ: "package:read",
  MENU_ITEM_READ: "menu_item:read",
  SYSTEM_CONFIG_READ: "system_config:read",
  AI_CHAT_READ: "ai_chat:read",
  BILLING_FEE_READ: "billing_fee:read",
  PAYMENT_GATEWAY_READ: "payment_gateway:read",
  EXCHANGE_RATE_READ: "exchange_rate:read",
  EXCHANGE_RATE_UPDATE: "exchange_rate:update",
  CANCELLATION_POLICY_READ: "cancellation_policy:read",
  CANCELLATION_POLICY_UPDATE: "cancellation_policy:update",
  REFUND_REQUEST_READ: "refund_request:read",
  REFUND_REQUEST_UPDATE: "refund_request:update",
  SYSTEM_CONFIGS_READ: "system_configs:read",
  SYSTEM_CONFIGS_UPDATE: "system_configs:update",
} as const;

export type PermissionCode = (typeof PERM)[keyof typeof PERM];

/**
 * Permission needed to open a route. Longest matching prefix wins.
 * Routes that are not listed are open to every signed-in admin.
 */
export const ROUTE_PERMISSIONS: Array<{ prefix: string; permission: PermissionCode }> = [
  { prefix: "/dashboard", permission: PERM.DASHBOARD_READ },
  { prefix: "/admin", permission: PERM.ADMIN_USER_READ },
  { prefix: "/app-users", permission: PERM.APP_USER_READ },
  { prefix: "/salon", permission: PERM.OUTLET_READ },
  { prefix: "/category", permission: PERM.CATEGORY_READ },
  { prefix: "/coupon", permission: PERM.COUPON_READ },
  { prefix: "/help-support", permission: PERM.SUPPORT_READ },
  { prefix: "/outlet-change-requests", permission: PERM.OUTLET_CHANGE_REQUEST_READ },
  { prefix: "/history-log", permission: PERM.LOG_READ },
  { prefix: "/orders", permission: PERM.ORDER_READ },
  { prefix: "/review-moderation", permission: PERM.REVIEW_MODERATE },
  { prefix: "/setting/roles", permission: PERM.ROLE_READ },
  { prefix: "/setting/language", permission: PERM.LANGUAGE_READ },
  { prefix: "/setting/multi-lang/app", permission: PERM.APP_TRANSLATION_READ },
  { prefix: "/setting/multi-lang/salon", permission: PERM.OWNER_TRANSLATION_READ },
  { prefix: "/setting/message-codes", permission: PERM.MESSAGE_CODE_READ },
  { prefix: "/setting/cancel-reasons", permission: PERM.CANCEL_REASON_READ },
  { prefix: "/setting/country", permission: PERM.COUNTRY_READ },
  { prefix: "/settings/packages", permission: PERM.PACKAGE_READ },
  { prefix: "/settings/menus", permission: PERM.MENU_ITEM_READ },
  { prefix: "/setting/system-config", permission: PERM.SYSTEM_CONFIG_READ },
  { prefix: "/setting/loyalty", permission: PERM.LOYALTY_READ },
  { prefix: "/setting/ai-chat", permission: PERM.AI_CHAT_READ },
  { prefix: "/setting/billing-fees", permission: PERM.BILLING_FEE_READ },
  { prefix: "/setting/payment-gateways", permission: PERM.PAYMENT_GATEWAY_READ },
  { prefix: "/setting/exchange-rates", permission: PERM.EXCHANGE_RATE_READ },
  { prefix: "/setting/cancellation-policies", permission: PERM.CANCELLATION_POLICY_READ },
  { prefix: "/setting/refund-requests", permission: PERM.REFUND_REQUEST_READ },
  { prefix: "/setting/system-configs", permission: PERM.SYSTEM_CONFIGS_READ },
];

export const requiredPermissionForPath = (pathname: string): PermissionCode | null => {
  const hit = ROUTE_PERMISSIONS.filter(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  ).sort((a, b) => b.prefix.length - a.prefix.length)[0];
  return hit ? hit.permission : null;
};
